import { clearLocalStorageItems, isTokenExpired } from './authUtils';

/**
 * Enhanced API call wrapper that checks token expiration before making requests
 * @param {Function} getIdTokenClaims - Auth0 function to get token claims
 * @param {Function} logout - Auth0 logout function
 * @param {Function} loginWithRedirect - Auth0 login redirect function
 * @returns {Function} - Enhanced API call function
 */
export const createApiCallWithTokenCheck = (getIdTokenClaims, logout, loginWithRedirect) => {
  return async (apiCallFunction) => {
    try {
      // Check token before making API call
      const tokenClaims = await getIdTokenClaims();
      
      if (!tokenClaims) {
        console.log("No token available - redirecting to login");
        clearLocalStorageItems();
        localStorage.setItem('redirectPath', window.location.pathname);
        loginWithRedirect();
        return null;
      }
      
      if (isTokenExpired(tokenClaims)) {
        console.log("Token expired before API call - logging out");
        clearLocalStorageItems();
        logout({ logoutParams: { returnTo: window.location.origin } });
        return null;
      }
      
      // Token is valid, proceed with API call
      return await apiCallFunction(tokenClaims.__raw);
      
    } catch (error) {
      console.error("Error in API call with token check:", error);
      
      // If it's an auth error, handle it
      if (error.response && error.response.status === 401) {
        console.log("API returned 401 - token may have expired during call");
        clearLocalStorageItems();
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
      
      throw error;
    }
  };
};

/**
 * Creates a standardized fetch wrapper with token validation
 * @param {string} idToken - The JWT token
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - Fetch promise
 */
export const fetchWithAuth = async (idToken, url, options = {}) => {
  const defaultHeaders = {
    'Authorization': `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
  
  const fetchOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };
  
  const response = await fetch(url, fetchOptions);
  
  if (!response.ok) {
    const error = new Error(`HTTP error! Status: ${response.status}`);
    error.response = response;
    throw error;
  }
  
  return response;
};
